import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { can } from '../lib/permissions'
import { ArrowLeft, Send, Loader2, AlertCircle, Clock, CheckCircle, XCircle, MessageSquare } from 'lucide-react'
import { ticketsAPI, helpdeskGroupsAPI, authAPI } from '@/lib/api'
import { io } from 'socket.io-client'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const CATEGORIES = {
  network: 'Netwerk',
  hardware: 'Hardware',
  gaming: 'Gaming',
  software: 'Software',
  other: 'Andere'
}

const PRIORITIES = {
  low: { label: 'Laag', color: 'bg-blue-500' },
  normal: { label: 'Normaal', color: 'bg-green-500' },
  high: { label: 'Hoog', color: 'bg-orange-500' },
  urgent: { label: 'Urgent', color: 'bg-red-500' }
}

const STATUS_CONFIG = {
  open: { label: 'Open', icon: AlertCircle, color: 'text-blue-500' },
  in_progress: { label: 'Bezig', icon: Clock, color: 'text-yellow-500' },
  waiting_user: { label: 'Wacht op u', icon: MessageSquare, color: 'text-purple-500' },
  resolved: { label: 'Opgelost', icon: CheckCircle, color: 'text-green-500' },
  closed: { label: 'Gesloten', icon: XCircle, color: 'text-gray-500' }
}

export default function TicketDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, can } = useAuth()
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [replying, setReplying] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [groups, setGroups] = useState([])
  const [updatingGroup, setUpdatingGroup] = useState(false)
  const [helpdeskUsers, setHelpdeskUsers] = useState([])
  const [updatingAssignee, setUpdatingAssignee] = useState(false)

  useEffect(() => {
    loadTicket()
  }, [id])

  // Socket.IO for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token || !id) return

    const socket = io({
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    })

    socket.on('connect', () => {
      console.log(`Connected to ticket ${id} socket`)
      socket.emit('join-ticket', id)
    })

    socket.on('ticket-reply-added', ({ ticketId, reply }) => {
      if (ticketId === parseInt(id)) {
        console.log('New reply received:', reply)
        loadTicket()
      }
    })

    socket.on('ticket-updated', ({ ticket }) => {
      if (ticket.id === parseInt(id)) {
        console.log('Ticket updated:', ticket)
        loadTicket()
      }
    })

    return () => {
      socket.emit('leave-ticket', id)
      socket.disconnect()
    }
  }, [id])

  // Load groups for helpdesk/admin
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const response = await helpdeskGroupsAPI.getGroups()
        console.log('Loaded groups:', response.data.groups)
        setGroups(response.data.groups || [])
      } catch (err) {
        console.error('Failed to load groups:', err)
      }
    }
    loadGroups()
  }, [])

  // Load helpdesk users for assignment
  useEffect(() => {
    const loadHelpdeskUsers = async () => {
      try {
        const response = await authAPI.searchUsers('')
        const helpdesk = response.data.users.filter(
          u => can(u.role, 'helpdesk_access')
        )
        console.log('Loaded helpdesk users:', helpdesk)
        setHelpdeskUsers(helpdesk)
      } catch (err) {
        console.error('Failed to load helpdesk users:', err)
      }
    }
    loadHelpdeskUsers()
  }, [])

  const loadTicket = async () => {
    try {
      setLoading(true)
      const response = await ticketsAPI.getTicket(id)
      setTicket({
        ...response.data.ticket,
        replies: response.data.replies || []
      })

      // Mark ticket as read
      try {
        await ticketsAPI.markAsRead(id)
      } catch (markReadErr) {
        console.error('Failed to mark ticket as read:', markReadErr)
        // Don't show error to user, just log it
      }
    } catch (err) {
      console.error('Failed to load ticket:', err)
      setError('Kon ticket niet laden')
    } finally {
      setLoading(false)
    }
  }

  const handleGroupChange = async (groupId) => {
    setUpdatingGroup(true)
    setError(null)

    try {
      // Convert 'none' to null for unassigning
      const finalGroupId = groupId === 'none' ? null : parseInt(groupId)
      await ticketsAPI.assignTicketToGroup(id, finalGroupId)
      setSuccess('Groep bijgewerkt')
      loadTicket()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update group:', err)
      setError(err.response?.data?.error || 'Kon groep niet bijwerken')
    } finally {
      setUpdatingGroup(false)
    }
  }

  const handleUserAssignment = async (userId) => {
    setUpdatingAssignee(true)
    setError(null)

    try {
      // Convert 'none' to null for unassigning
      const finalUserId = userId === 'none' ? null : parseInt(userId)
      await ticketsAPI.assignTicket(id, finalUserId)
      setSuccess('Toegewezen gebruiker bijgewerkt')
      loadTicket()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update assignee:', err)
      setError(err.response?.data?.error || 'Kon toegewezen gebruiker niet bijwerken')
    } finally {
      setUpdatingAssignee(false)
    }
  }

  const handleAddReply = async (e) => {
    e.preventDefault()
    if (!replyText.trim()) return

    setReplying(true)
    setError(null)

    try {
      await ticketsAPI.addReply(id, { message: replyText })
      setSuccess('Reactie toegevoegd')
      setReplyText('')
      loadTicket()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to add reply:', err)
      setError(err.response?.data?.error || 'Kon reactie niet toevoegen')
    } finally {
      setReplying(false)
    }
  }

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true)
    setError(null)

    try {
      await ticketsAPI.updateStatus(id, newStatus)
      setSuccess('Status bijgewerkt')
      loadTicket()
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Failed to update status:', err)
      setError(err.response?.data?.error || 'Kon status niet bijwerken')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-neon-green" />
        </div>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Ticket niet gevonden</AlertDescription>
        </Alert>
      </div>
    )
  }

  const StatusIcon = STATUS_CONFIG[ticket.status]?.icon || AlertCircle
  const statusColor = STATUS_CONFIG[ticket.status]?.color || 'text-gray-500'
  const isTicketOwner = user?.id === ticket.user_id
  const isHelpdeskOrAdmin = can(user?.role, 'helpdesk_access')
  const canChangeStatus = isTicketOwner || isHelpdeskOrAdmin

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/helpdesk')}
          className="mb-4 hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Terug naar tickets
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="text-xs">
                #{ticket.id}
              </Badge>
              <div className={`h-2 w-2 rounded-full ${PRIORITIES[ticket.priority]?.color || 'bg-gray-500'}`} />
              <span className="text-xs text-muted-foreground">
                {PRIORITIES[ticket.priority]?.label}
              </span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">
                {CATEGORIES[ticket.category]}
              </span>
            </div>
            <h1 className="text-4xl font-bold text-neon-green mb-2">{ticket.title}</h1>
            <p className="text-muted-foreground">
              Aangemaakt door {ticket.creator_display_name} op {formatDate(ticket.created_at)}
            </p>
            {ticket.location && (
              <p className="text-muted-foreground">📍 {ticket.location}</p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className={`flex items-center gap-2 ${statusColor}`}>
              <StatusIcon className="h-5 w-5" />
              <span className="text-lg font-medium">{STATUS_CONFIG[ticket.status]?.label}</span>
            </div>
            {ticket.assignee_display_name && (
              <Badge variant="secondary">
                Toegewezen aan: {ticket.assignee_display_name}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <Alert className="mb-6 border-neon-green bg-neon-green/10">
          <CheckCircle className="h-4 w-4 text-neon-green" />
          <AlertDescription className="text-neon-green">{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Ticket Description */}
      <Card className="border-neon-purple/30 mb-6">
        <CardHeader>
          <CardTitle>Beschrijving</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="whitespace-pre-wrap">{ticket.description}</p>
        </CardContent>
      </Card>

      {/* Status Management */}
      {canChangeStatus && ticket.status !== 'closed' && (
        <Card className="border-neon-purple/30 mb-6">
          <CardHeader>
            <CardTitle>Status Beheer</CardTitle>
            <CardDescription>
              Wijzig de status van dit ticket
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Select
                value={ticket.status}
                onValueChange={handleStatusChange}
                disabled={updatingStatus}
              >
                <SelectTrigger className="w-[200px] bg-background border-neon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">Bezig</SelectItem>
                  <SelectItem value="waiting_user">Wacht op u</SelectItem>
                  <SelectItem value="resolved">Opgelost</SelectItem>
                  <SelectItem value="closed">Gesloten</SelectItem>
                </SelectContent>
              </Select>
              {updatingStatus && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Group Assignment (Helpdesk/Admin only) */}
      {isHelpdeskOrAdmin && ticket.status !== 'closed' && (
        <Card className="border-neon-purple/30 mb-6">
          <CardHeader>
            <CardTitle>Groep Toewijzing</CardTitle>
            <CardDescription>
              Wijs dit ticket toe aan een helpdesk groep
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Select
                value={ticket.assigned_group_id?.toString() || 'none'}
                onValueChange={handleGroupChange}
                disabled={updatingGroup}
              >
                <SelectTrigger className="w-[250px] bg-background border-neon">
                  <SelectValue placeholder="Geen groep geselecteerd" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen groep</SelectItem>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id.toString()}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {updatingGroup && <Loader2 className="h-4 w-4 animate-spin" />}
              {ticket.group_name && (
                <Badge
                  variant="outline"
                  style={{
                    borderColor: ticket.group_color,
                    color: ticket.group_color
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full mr-2"
                    style={{ backgroundColor: ticket.group_color }}
                  />
                  {ticket.group_name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Assignment (Helpdesk/Admin only) */}
      {isHelpdeskOrAdmin && ticket.status !== 'closed' && (
        <Card className="border-neon-purple/30 mb-6">
          <CardHeader>
            <CardTitle>Gebruiker Toewijzing</CardTitle>
            <CardDescription>
              Wijs dit ticket toe aan een helpdesk medewerker
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Select
                value={ticket.assigned_to?.toString() || 'none'}
                onValueChange={handleUserAssignment}
                disabled={updatingAssignee}
              >
                <SelectTrigger className="w-[250px] bg-background border-neon">
                  <SelectValue placeholder="Geen gebruiker geselecteerd" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Niet toegewezen</SelectItem>
                  {helpdeskUsers.map((helpdeskUser) => (
                    <SelectItem key={helpdeskUser.id} value={helpdeskUser.id.toString()}>
                      {helpdeskUser.display_name || helpdeskUser.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {updatingAssignee && <Loader2 className="h-4 w-4 animate-spin" />}
              {ticket.assignee_display_name && (
                <Badge variant="secondary">
                  Toegewezen aan: {ticket.assignee_display_name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Replies */}
      <Card className="border-neon-purple/30 mb-6">
        <CardHeader>
          <CardTitle>
            Reacties ({ticket.replies?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ticket.replies && ticket.replies.length > 0 ? (
            <div className="space-y-4">
              {ticket.replies.map((reply) => (
                <div
                  key={reply.id}
                  className="border border-neon-purple/20 rounded-lg p-4 bg-background/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-neon-green">
                        {reply.display_name}
                      </span>
                      {(['admin', 'superadmin', 'helpdesk'].includes(reply.role)) && (
                        <Badge variant="secondary" className="text-xs">
                          Staff
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(reply.created_at)}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{reply.message}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Nog geen reacties op dit ticket
            </p>
          )}
        </CardContent>
      </Card>

      {/* Add Reply */}
      {ticket.status !== 'closed' && (
        <Card className="border-neon-purple/30">
          <CardHeader>
            <CardTitle>Reactie Toevoegen</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddReply} className="space-y-4">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Typ je bericht..."
                rows={4}
                className="bg-background border-neon"
                disabled={replying}
              />
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={replying || !replyText.trim()}
                  className="bg-neon-green text-background hover:bg-neon-green/90"
                >
                  {replying ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Versturen...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Versturen
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {ticket.status === 'closed' && (
        <Alert>
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            Dit ticket is gesloten. Geen nieuwe reacties mogelijk.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}
